import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Spinner } from '@heroui/react';
import { getNodeTestIdProp, useIsNarrowViewport } from '@/utils';
import {
    UNIT_LABEL_KEY,
    UNIT_OPTIONS,
} from '../createProduct/createProductMeta';
import {
    resolveFilterWords,
    type FilterWords,
} from '../productFilters/filterLabels';
import { PRODUCT_TYPE_CHIP } from '../productsMeta';
import {
    EXPORTED_AT_LABEL_KEY,
    PRODUCT_EXPORT_COLUMNS,
    type ProductExportLabels,
} from './productExportMeta';
import type { ProductExportState } from './useProductExport';

type ProductExportButtonProps = Pick<
    ProductExportState,
    'isDisabled' | 'isExporting' | 'filterLines' | 'onExport'
>;

/**
 * The toolbar's Export Excel (bos-app#120, design board **Main**; "Export" on
 * phones, board **Mobile**). Off while the list matches nothing, a spinner
 * while a run is under way.
 *
 * On press it puts the workbook's copy into the reader's language, the one
 * part of the export that needs `t()`, and hands it to the hook.
 */
export const ProductExportButton: React.FC<ProductExportButtonProps> = ({
    isDisabled,
    isExporting,
    filterLines,
    onExport,
}) => {
    const { t } = useTranslation('pages');
    const isNarrow = useIsNarrowViewport();

    const wordsOf = (words: FilterWords): string =>
        resolveFilterWords(words, (key, values) => t(key, values));

    const onPress = () => {
        const labels: ProductExportLabels = {
            sheets: {
                products: t('products.export.workbook.sheets.products'),
                filters: t('products.export.workbook.sheets.filters'),
            },
            columns: Object.fromEntries(
                PRODUCT_EXPORT_COLUMNS.map((column) => [
                    column.key,
                    t(column.labelKey),
                ]),
            ),
            types: {
                Simple: t(PRODUCT_TYPE_CHIP.Simple.labelKey),
                Variable: t(PRODUCT_TYPE_CHIP.Variable.labelKey),
            },
            units: Object.fromEntries(
                UNIT_OPTIONS.map((unit) => [unit, t(UNIT_LABEL_KEY[unit])]),
            ),
            yes: t('products.export.workbook.yes'),
            no: t('products.export.workbook.no'),
            categorySeparator: t(
                'products.components.productTable.filters.categoryPathSeparator',
            ),
            filters: {
                heading: {
                    label: t('products.export.filters.heading.filter'),
                    value: t('products.export.filters.heading.value'),
                },
                lines: filterLines.map((line) => ({
                    label: t(line.labelKey),
                    value: line.values.map(wordsOf).join(', '),
                })),
                exportedAt: t(EXPORTED_AT_LABEL_KEY),
            },
        };
        onExport(labels);
    };

    return (
        <Button
            isDisabled={isDisabled}
            isPending={isExporting}
            onPress={onPress}
            variant="secondary"
            {...getNodeTestIdProp('product-table-export')}
        >
            {({ isPending }) => (
                <>
                    {isPending ? (
                        <Spinner color="current" size="sm" />
                    ) : (
                        <Icon aria-hidden icon="gravity-ui:file-arrow-down" />
                    )}
                    {isNarrow
                        ? t('products.export.buttonShort')
                        : t('products.export.button')}
                </>
            )}
        </Button>
    );
};
